import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Upper bound on `forceSave`'s drain-the-queue wait. A save only ever queues a
 * single follow-up, so this just guards against an unbounded wait if a debounce
 * keeps re-arming (e.g. the user types continuously during the wait).
 */
const FORCE_SAVE_MAX_DRAIN = 10;

interface UseAutoSaveOptions {
  /** Delay in milliseconds before auto-save triggers */
  delay?: number;
  /** Function to call for saving */
  onSave: () => Promise<boolean>;
  /** Dependencies to watch for changes */
  dependencies: unknown[];
  /**
   * Whether edits self-save on the debounce. Defaults to `true`.
   *
   * Set `false` for manual mode: dirty state is still tracked and `saveNow` /
   * `forceSave` still work, but nothing writes until a caller asks it to, and
   * unmount stops flushing. That is what lets a form batch edits behind an
   * explicit Save button instead of persisting every keystroke.
   *
   * Expected to be constant for the hook's lifetime; flipping it mid-life does
   * not retroactively cancel an already-scheduled debounce.
   */
  auto?: boolean;
}

interface UseAutoSaveReturn {
  isSaving: boolean;
  lastSaved: Date | null;
  hasUnsavedChanges: boolean;
  saveNow: () => Promise<boolean>;
  /**
   * Cancel the pending debounce, wait out any in-flight save (and the single
   * follow-up it may queue), then run one fresh save of the LATEST state and
   * resolve with its success. Unlike `saveNow`, this never no-ops or drops the
   * write behind an in-flight save — so callers can compose a guaranteed
   * save-the-current-state step (e.g. flush-on-close). Failure of the final
   * save resolves `false`.
   */
  forceSave: () => Promise<boolean>;
  /**
   * Read the live (synchronous) save state straight from the internal refs,
   * not the one-render-delayed React state. Stable identity — lets a composing
   * hook check `isSaving` / `hasUnsavedChanges` mid-flush without mirroring it.
   */
  getLiveState: () => { isSaving: boolean; hasUnsavedChanges: boolean };
  /** Mark as synced (e.g. after applying remote changes) — updates lastSaved and clears unsaved flag */
  markAsSynced: () => void;
}

/**
 * Debounced auto-save with re-entrancy handling. Watches `dependencies` and,
 * after `delay` ms of quiet, calls `onSave`; coalesces rapid edits, queues a
 * follow-up if one lands mid-save, and flushes on unmount.
 *
 * @param opts - `delay` (ms), `onSave` (returns true on success), and the
 *   `dependencies` array to watch.
 * @returns Save state plus `saveNow`, `forceSave`, `getLiveState`, and `markAsSynced`.
 */
export const useAutoSave = ({
  delay = 2000,
  onSave,
  dependencies,
  auto = true,
}: UseAutoSaveOptions): UseAutoSaveReturn => {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const initialLoadRef = useRef(true);

  // Refs so save logic always reads live values — never stale closure snapshots
  const isSavingRef = useRef(false);
  const hasPendingSaveRef = useRef(false);
  const hasUnsavedChangesRef = useRef(false);
  const onSaveRef = useRef(onSave);
  // Read by the unmount cleanup, whose closure is captured at mount.
  const autoRef = useRef(auto);
  // Promise for the currently in-flight save, so `flush()` can await the exact
  // round-trip instead of polling. Null when no save is running.
  const inFlightRef = useRef<Promise<boolean> | null>(null);

  // Keep onSaveRef in sync on every render (no dep array = after every render)
  useEffect(() => {
    onSaveRef.current = onSave;
    autoRef.current = auto;
  });

  const saveNow = useCallback((): Promise<boolean> => {
    // If a save is already in flight, mark a pending save so the latest data
    // gets written once the current one completes — don't silently drop the write.
    if (isSavingRef.current) {
      hasPendingSaveRef.current = true;
      return Promise.resolve(false);
    }

    isSavingRef.current = true;
    setIsSaving(true);

    const run = async (): Promise<boolean> => {
      try {
        const success = await onSaveRef.current();
        if (success) {
          hasUnsavedChangesRef.current = false;
          setLastSaved(new Date());
          setHasUnsavedChanges(false);
        }
        return success;
      } catch (error) {
        console.error("Auto-save failed:", error);
        return false;
      } finally {
        isSavingRef.current = false;
        setIsSaving(false);
        inFlightRef.current = null;

        // Flush any write that was queued while this save was in flight
        if (hasPendingSaveRef.current) {
          hasPendingSaveRef.current = false;
          saveNow();
        }
      }
    };

    const promise = run();
    inFlightRef.current = promise;
    return promise;
  }, []); // stable — uses refs for all live values

  const forceSave = useCallback(async (): Promise<boolean> => {
    // Cancel the pending debounce so it can't fire a redundant save mid-flush.
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Wait out any in-flight save (and the follow-up its `finally` may queue) so
    // the save below runs fresh against the latest state instead of being
    // dropped by the re-entrancy guard.
    let drained = 0;
    while (inFlightRef.current && drained < FORCE_SAVE_MAX_DRAIN) {
      await inFlightRef.current;
      drained += 1;
    }

    // `onSaveRef` always points at the latest closure, so this persists the
    // newest state regardless of what any earlier in-flight save captured.
    return saveNow();
  }, [saveNow]);

  useEffect(() => {
    // Skip auto-save on initial load
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      return;
    }

    hasUnsavedChangesRef.current = true;
    setHasUnsavedChanges(true);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    // Manual mode tracks the edit but schedules nothing; the caller decides when.
    if (!auto) return;

    timeoutRef.current = setTimeout(() => {
      saveNow();
    }, delay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, dependencies);

  // Flush any pending save on unmount so navigating away doesn't silently drop
  // writes. Manual mode deliberately does NOT: an explicit-save form that
  // persisted on unmount anyway would make the Save button a lie, and a user who
  // dismissed the unsaved-changes guard has chosen not to save.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (autoRef.current && hasUnsavedChangesRef.current) {
        saveNow();
      }
    };
  }, [saveNow]);

  const getLiveState = useCallback(
    () => ({ isSaving: isSavingRef.current, hasUnsavedChanges: hasUnsavedChangesRef.current }),
    []
  );

  const markAsSynced = useCallback(() => {
    hasUnsavedChangesRef.current = false;
    setLastSaved(new Date());
    setHasUnsavedChanges(false);
  }, []);

  return {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    saveNow,
    forceSave,
    getLiveState,
    markAsSynced,
  };
};
